import { c as _c } from "react/compiler-runtime";
import { Field, useField, useFormikContext } from 'formik';
import React, { ReactElement, memo, useState } from 'react';
import ImageComponent from '@spa/components/image-component/image.component';
import { InputDataAttributes } from '@spa/components/form-group/InputDataAttributes';
import InputDateComponent from '@spa/components/form-group/input-date.component';
import InputGiftCardIdComponent from '@spa/components/form-group/input-gift-card-id.component';
import { InputMode } from '@spa/components/form-group/InputMode';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import PasswordConditionsComponent from '../forms/password-conditions/password-conditions.component';
import classNames from 'clsx';
import logger from '@spa/lib/logger';
import styles from './form-group.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export enum InputType {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  EMAIL = 'email',
  GIFT_CARD_ID = 'gift-card-id',
  PASSWORD = 'password',
  RADIO = 'radio',
  SELECT = 'select',
  TEL = 'tel',
  TEXT = 'text',
  TEXTAREA = 'textarea',
}
const textLikeTypes = [InputType.TEXT, InputType.EMAIL, InputType.PASSWORD, InputType.DATE, InputType.GIFT_CARD_ID, InputType.TEL] as const;
export interface RadioOption {
  value: string | number;
  label: string;
}
export type HTMLFormFieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
export interface Props {
  readonly id: string;
  readonly error?: string;
  readonly errorBtn?: ReactElement;
  readonly inputButton?: ReactElement;
  readonly showPasswordReuseCondition?: boolean;
  readonly username?: string;
  readonly touched?: boolean;
  readonly label: string | ReactElement;
  readonly name: string;
  readonly checked?: boolean;
  readonly selectOptions?: ReadonlyArray<SelectOption>;
  readonly radioOptions?: ReadonlyArray<RadioOption>;
  readonly placeholder?: string;
  readonly showValueToggleIcon?: boolean;
  readonly type: string | InputType;
  readonly value?: string;
  readonly centerText?: boolean;
  readonly displayRequirements?: boolean;
  readonly halfWidth?: boolean;
  readonly hiddenLabel?: boolean;
  readonly inputMode?: InputMode;
  readonly isShrunk?: boolean;
  readonly isReadOnly?: boolean;
  readonly isDisabled?: boolean;
  readonly isAutocompleteEnabled?: boolean;
  readonly maxLength?: number;
  readonly required?: boolean;
  readonly hideRequiredAsterisk?: boolean;
  readonly autoComplete?: string;
  readonly inputDataAttributes?: InputDataAttributes;
  readonly tabIndex?: number;
  readonly onSelectCallback?: () => void;
  readonly onChangeCallback?: (event?: React.ChangeEvent<HTMLFormFieldElement>) => Promise<void> | void;
  readonly onBlurCallback?: (() => void) | (() => Promise<void>);
  readonly onFocusCallback?: () => void;
}
interface Icon {
  readonly width: number;
  readonly height: number;
  readonly path: string;
  readonly alt: string;
}
export interface SelectOption {
  readonly value: string;
  readonly name: string;
}
function FormGroupField(t0) {
  const $ = _c(60);
  const {
    className,
    placeholder,
    name,
    value,
    isDisabled,
    isReadOnly,
    autoComplete,
    inputDataAttributes,
    inputMode,
    maxLength,
    type,
    passwordInputType,
    required,
    displayRequirements,
    setShowRequirements,
    onChangeCallback,
    onBlurCallback,
    onFocusCallback,
    onSelectCallback,
    inputId,
    selectOptions,
    option,
    fieldId,
    describedById,
    hasError,
    tabIndex
  } = t0;
  const formik = useFormikContext();
  let t1;
  if ($[0] !== formik || $[1] !== onChangeCallback) {
    t1 = event => {
      if (onChangeCallback) {
        Promise.resolve(onChangeCallback(event)).catch(_temp);
      }
      formik.handleChange(event);
    };
    $[0] = formik;
    $[1] = onChangeCallback;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleChange = t1;
  let t2;
  if ($[3] !== formik || $[4] !== onBlurCallback) {
    t2 = event_0 => {
      if (onBlurCallback) {
        Promise.resolve(onBlurCallback()).catch(_temp2);
      }
      formik.handleBlur(event_0);
    };
    $[3] = formik;
    $[4] = onBlurCallback;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleBlur = t2;
  let t3;
  if ($[6] !== onFocusCallback) {
    t3 = () => {
      if (onFocusCallback) {
        onFocusCallback();
      }
    };
    $[6] = onFocusCallback;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleFocus = t3;
  let t4;
  if ($[8] !== autoComplete || $[9] !== className || $[10] !== inputDataAttributes || $[11] !== isDisabled || $[12] !== isReadOnly || $[13] !== name || $[14] !== placeholder || $[15] !== tabIndex || $[16] !== value) {
    t4 = {
      ...inputDataAttributes,
      className,
      placeholder,
      name,
      value,
      disabled: isDisabled,
      readOnly: isReadOnly,
      autoComplete,
      tabIndex
    };
    $[8] = autoComplete;
    $[9] = className;
    $[10] = inputDataAttributes;
    $[11] = isDisabled;
    $[12] = isReadOnly;
    $[13] = name;
    $[14] = placeholder;
    $[15] = tabIndex;
    $[16] = value;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const commonProps = t4;
  let render;
  bb0: switch (type) {
    case InputType.PASSWORD:
    case InputType.EMAIL:
    case InputType.TEXT:
    case InputType.TEL:
      {
        let t5;
        if ($[18] !== inputMode) {
          t5 = inputMode ? {
            inputMode
          } : {};
          $[18] = inputMode;
          $[19] = t5;
        } else {
          t5 = $[19];
        }
        const t6 = type === InputType.PASSWORD ? passwordInputType : type;
        const t7 = required || undefined;
        const t8 = hasError || undefined;
        let t9;
        if ($[20] !== displayRequirements || $[21] !== handleFocus || $[22] !== setShowRequirements || $[23] !== type) {
          t9 = () => {
            if (type === InputType.PASSWORD && displayRequirements) {
              setShowRequirements(true);
            }
            handleFocus();
          };
          $[20] = displayRequirements;
          $[21] = handleFocus;
          $[22] = setShowRequirements;
          $[23] = type;
          $[24] = t9;
        } else {
          t9 = $[24];
        }
        let t10;
        if ($[25] !== commonProps || $[26] !== describedById || $[27] !== handleBlur || $[28] !== handleChange || $[29] !== maxLength || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
          t10 = <Field {...commonProps} {...t5} maxLength={maxLength} type={t6} aria-required={t7} aria-describedby={describedById} aria-invalid={t8} onFocus={t9} onChange={handleChange} onBlur={handleBlur} />;
          $[25] = commonProps;
          $[26] = describedById;
          $[27] = handleBlur;
          $[28] = handleChange;
          $[29] = maxLength;
          $[30] = t5;
          $[31] = t6;
          $[32] = t7;
          $[33] = t8;
          $[34] = t9;
          $[35] = t10;
        } else {
          t10 = $[35];
        }
        render = t10;
        break bb0;
      }
    case InputType.TEXTAREA:
      {
        let t5;
        if ($[36] !== commonProps) {
          t5 = <Field {...commonProps} as="textarea" />;
          $[36] = commonProps;
          $[37] = t5;
        } else {
          t5 = $[37];
        }
        render = t5;
        break bb0;
      }
    case InputType.SELECT:
      {
        let t5;
        if ($[38] !== selectOptions) {
          t5 = selectOptions?.map(_temp3);
          $[38] = selectOptions;
          $[39] = t5;
        } else {
          t5 = $[39];
        }
        let t6;
        if ($[40] !== commonProps || $[41] !== handleChange || $[42] !== t5) {
          t6 = <Field {...commonProps} as="select" onChange={handleChange}>{t5}</Field>;
          $[40] = commonProps;
          $[41] = handleChange;
          $[42] = t5;
          $[43] = t6;
        } else {
          t6 = $[43];
        }
        render = t6;
        break bb0;
      }
    case InputType.RADIO:
      {
        const t5 = option?.value;
        const t6 = option?.value === value;
        let t7;
        if ($[44] !== fieldId || $[45] !== isDisabled || $[46] !== isReadOnly || $[47] !== name || $[48] !== onSelectCallback || $[49] !== t5 || $[50] !== t6) {
          t7 = <Field id={fieldId} type="radio" name={name} value={t5} readOnly={isReadOnly} onClick={onSelectCallback} checked={t6} disabled={isDisabled} />;
          $[44] = fieldId;
          $[45] = isDisabled;
          $[46] = isReadOnly;
          $[47] = name;
          $[48] = onSelectCallback;
          $[49] = t5;
          $[50] = t6;
          $[51] = t7;
        } else {
          t7 = $[51];
        }
        render = t7;
        break bb0;
      }
    case InputType.CHECKBOX:
      {
        let t5;
        if ($[52] !== commonProps || $[53] !== handleChange || $[54] !== inputId) {
          t5 = <Field type="checkbox" {...commonProps} id={inputId} onChange={handleChange} />;
          $[52] = commonProps;
          $[53] = handleChange;
          $[54] = inputId;
          $[55] = t5;
        } else {
          t5 = $[55];
        }
        render = t5;
        break bb0;
      }
    case InputType.DATE:
      {
        let t5;
        if ($[56] !== commonProps) {
          t5 = <InputDateComponent {...commonProps} />;
          $[56] = commonProps;
          $[57] = t5;
        } else {
          t5 = $[57];
        }
        render = t5;
        break bb0;
      }
    case InputType.GIFT_CARD_ID:
      {
        let t5;
        if ($[58] !== commonProps) {
          t5 = <InputGiftCardIdComponent {...commonProps} />;
          $[58] = commonProps;
          $[59] = t5;
        } else {
          t5 = $[59];
        }
        render = t5;
        break bb0;
      }
    default:
      {
        throw new Error(`Unsupported input type: '${type}'`);
      }
  }
  return render;
}
function _temp3(t0) {
  const {
    value: value_0,
    name: name_0
  } = t0;
  return <option key={value_0} value={value_0}>{name_0}</option>;
}
function _temp2(error_0) {
  logger.error({
    error: error_0
  }, "Form blur callback failed");
}
function _temp(error) {
  logger.error({
    error
  }, "Form change callback failed");
}
function FormGroupErrorText(t0) {
  const $ = _c(12);
  const {
    id,
    error,
    touched,
    type,
    name,
    isAutocompleteEnabled,
    errorBtn
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("form-group");
  const [, t1] = useField(name);
  const {
    value: fieldMetaValue
  } = t1;
  let errorContent = null;
  if (error && touched && (type !== InputType.CHECKBOX || !fieldMetaValue) && !isAutocompleteEnabled) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = classNames(styles["form-group__error-text"], "text-s");
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== formatMessage) {
      t3 = formatMessage("warningIcon");
      $[1] = formatMessage;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t3) {
      t4 = <ImageComponent className={styles["form-group__error-text__icon"]} src="/assets/icons/warning.svg" alt={t3} width={20} height={20} />;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== error || $[6] !== errorBtn) {
      t5 = <span>{error} {errorBtn}</span>;
      $[5] = error;
      $[6] = errorBtn;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== id || $[9] !== t4 || $[10] !== t5) {
      t6 = <span id={id} className={t2}>{t4}{t5}</span>;
      $[8] = id;
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    errorContent = t6;
  }
  return errorContent;
}
function FormGroupComponent(t0) {
  const $ = _c(102);
  const {
    id,
    error,
    errorBtn,
    inputButton,
    touched,
    label,
    name,
    selectOptions,
    radioOptions,
    placeholder,
    required: t1,
    hideRequiredAsterisk: t2,
    showValueToggleIcon,
    type,
    value,
    centerText,
    displayRequirements,
    halfWidth,
    hiddenLabel,
    inputMode,
    isShrunk,
    isReadOnly: t3,
    isAutocompleteEnabled: t4,
    isDisabled,
    maxLength,
    autoComplete,
    inputDataAttributes,
    tabIndex,
    onSelectCallback,
    onChangeCallback,
    onBlurCallback,
    onFocusCallback,
    showPasswordReuseCondition,
    username
  } = t0;
  const required = t1 === undefined ? false : t1;
  const hideRequiredAsterisk = t2 === undefined ? false : t2;
  const isReadOnly = t3 === undefined ? false : t3;
  const isAutocompleteEnabled = t4 === undefined ? false : t4;
  const inputId = `${id}-${name}`;
  const {
    formatMessage
  } = useNamespacedIntl("form-group");
  const [showPassword, setShowPassword] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const showPasswordConditions = type === InputType.PASSWORD && displayRequirements === true && (showRequirements || (value ?? "") !== "" || touched === true && error !== undefined);
  const passwordConditionsId = `${inputId}-conditions`;
  const errorTextId = `${inputId}-error`;
  const hasError = touched === true && error !== undefined;
  const describedById = showPasswordConditions ? passwordConditionsId : hasError && !isAutocompleteEnabled ? errorTextId : undefined;
  const passwordInputType = showPassword ? "text" : type;
  let getGroupClass;
  let getLabelClass;
  let hasInputButton;
  let inputClassName;
  let inputWrapperClassName;
  let labelClass;
  let showPasswordIcon;
  let t5;
  if ($[0] !== centerText || $[1] !== error || $[2] !== formatMessage || $[3] !== halfWidth || $[4] !== hiddenLabel || $[5] !== inputButton || $[6] !== isDisabled || $[7] !== isShrunk || $[8] !== selectOptions || $[9] !== showPassword || $[10] !== touched || $[11] !== type || $[12] !== value) {
    const isTextLike = textLikeTypes.includes(type as (typeof textLikeTypes)[number]);
    hasInputButton = isTextLike && inputButton !== undefined;
    const t6 = `showPasswordIcon.${showPassword ? "hide" : "show"}PasswordAlt`;
    let t7;
    if ($[21] !== formatMessage || $[22] !== t6) {
      t7 = formatMessage(t6);
      $[21] = formatMessage;
      $[22] = t6;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    const iconAlt = t7;
    const t8 = showPassword ? "/assets/icons/show-password-icon.svg" : "/assets/icons/hide-password-icon.svg";
    let t9;
    if ($[24] !== iconAlt || $[25] !== t8) {
      t9 = {
        path: t8,
        width: 15,
        height: 15,
        alt: iconAlt
      };
      $[24] = iconAlt;
      $[25] = t8;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    showPasswordIcon = t9;
    const getInputClass = () => classNames(styles["form-group__input"], {
      [styles["form-group__input--textarea"]]: type === InputType.TEXTAREA,
      [styles["form-group__input--checkbox"]]: type === InputType.CHECKBOX,
      [styles["form-group__input--required"]]: touched && error,
      [styles["form-group__input--half-width"]]: halfWidth && !hasInputButton,
      [styles["form-group__input--hiddenLabel"]]: hiddenLabel,
      [styles["form-group__input--center-text"]]: centerText,
      [styles["form-group__input--disabled"]]: isDisabled,
      [styles["form-group__input--with-button"]]: hasInputButton
    });
    let t10;
    if ($[27] !== selectOptions || $[28] !== type || $[29] !== value) {
      const selectedOptionName = type === InputType.SELECT ? selectOptions?.find(option => option.value === value)?.name : undefined;
      t10 = type === InputType.SELECT ? Boolean(selectedOptionName && selectedOptionName.trim().length > 0) : Boolean(value && value.toString().length > 0 && type !== InputType.CHECKBOX);
      $[27] = selectOptions;
      $[28] = type;
      $[29] = value;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    const isFilled = t10;
    let t11;
    if ($[31] !== isFilled || $[32] !== isShrunk || $[33] !== type) {
      t11 = () => classNames(styles["form-group"], {
        [styles["form-group--expanded"]]: type !== InputType.CHECKBOX,
        [styles["form-group--shrunk"]]: isShrunk,
        [styles["form-group--filled"]]: isFilled,
        [styles["form-group--thick"]]: type === InputType.CHECKBOX
      });
      $[31] = isFilled;
      $[32] = isShrunk;
      $[33] = type;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    getGroupClass = t11;
    let t12;
    if ($[35] !== type) {
      t12 = () => classNames(styles["form-group__input-label"], {
        [styles["form-group__input-label--radio"]]: type === InputType.RADIO,
        [styles["form-group__input-label--checkbox"]]: type === InputType.CHECKBOX,
        [styles["form-group__input-label--textarea"]]: type === InputType.TEXTAREA
      });
      $[35] = type;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    getLabelClass = t12;
    let t13;
    if ($[37] !== hiddenLabel) {
      t13 = classNames(styles["form-group__input-label-text"], "text-base-immutable", {
        "sr-only": hiddenLabel
      });
      $[37] = hiddenLabel;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    labelClass = t13;
    inputClassName = getInputClass();
    inputWrapperClassName = classNames(styles["form-group__input-wrapper"], {
      [styles["form-group__input-wrapper--with-button"]]: hasInputButton,
      [styles["form-group__input-wrapper--half-width"]]: halfWidth && hasInputButton
    });
    t5 = classNames(styles["form-group__input-field"], {
      [styles["form-group__input-field--with-button"]]: hasInputButton
    });
    $[0] = centerText;
    $[1] = error;
    $[2] = formatMessage;
    $[3] = halfWidth;
    $[4] = hiddenLabel;
    $[5] = inputButton;
    $[6] = isDisabled;
    $[7] = isShrunk;
    $[8] = selectOptions;
    $[9] = showPassword;
    $[10] = touched;
    $[11] = type;
    $[12] = value;
    $[13] = getGroupClass;
    $[14] = getLabelClass;
    $[15] = hasInputButton;
    $[16] = inputClassName;
    $[17] = inputWrapperClassName;
    $[18] = labelClass;
    $[19] = showPasswordIcon;
    $[20] = t5;
  } else {
    getGroupClass = $[13];
    getLabelClass = $[14];
    hasInputButton = $[15];
    inputClassName = $[16];
    inputWrapperClassName = $[17];
    labelClass = $[18];
    showPasswordIcon = $[19];
    t5 = $[20];
  }
  const inputFieldClassName = t5;
  let t6;
  if ($[39] !== autoComplete || $[40] !== describedById || $[41] !== displayRequirements || $[42] !== hasError || $[43] !== inputClassName || $[44] !== inputDataAttributes || $[45] !== inputId || $[46] !== inputMode || $[47] !== isDisabled || $[48] !== isReadOnly || $[49] !== maxLength || $[50] !== name || $[51] !== onBlurCallback || $[52] !== onChangeCallback || $[53] !== onFocusCallback || $[54] !== onSelectCallback || $[55] !== passwordInputType || $[56] !== placeholder || $[57] !== required || $[58] !== selectOptions || $[59] !== tabIndex || $[60] !== type || $[61] !== value) {
    t6 = {
      className: inputClassName,
      placeholder,
      name,
      value,
      isDisabled,
      isReadOnly,
      autoComplete,
      inputDataAttributes,
      inputMode,
      maxLength,
      type,
      passwordInputType,
      required,
      displayRequirements,
      setShowRequirements,
      onChangeCallback,
      onBlurCallback,
      onFocusCallback,
      onSelectCallback,
      inputId,
      selectOptions,
      describedById,
      hasError,
      tabIndex
    };
    $[39] = autoComplete;
    $[40] = describedById;
    $[41] = displayRequirements;
    $[42] = hasError;
    $[43] = inputClassName;
    $[44] = inputDataAttributes;
    $[45] = inputId;
    $[46] = inputMode;
    $[47] = isDisabled;
    $[48] = isReadOnly;
    $[49] = maxLength;
    $[50] = name;
    $[51] = onBlurCallback;
    $[52] = onChangeCallback;
    $[53] = onFocusCallback;
    $[54] = onSelectCallback;
    $[55] = passwordInputType;
    $[56] = placeholder;
    $[57] = required;
    $[58] = selectOptions;
    $[59] = tabIndex;
    $[60] = type;
    $[61] = value;
    $[62] = t6;
  } else {
    t6 = $[62];
  }
  const fieldProps = t6 as const;
  let t7;
  if ($[63] !== getGroupClass) {
    t7 = getGroupClass();
    $[63] = getGroupClass;
    $[64] = t7;
  } else {
    t7 = $[64];
  }
  let t8;
  if ($[65] !== fieldProps || $[66] !== getLabelClass || $[67] !== hasInputButton || $[68] !== hideRequiredAsterisk || $[69] !== inputButton || $[70] !== inputFieldClassName || $[71] !== inputId || $[72] !== inputWrapperClassName || $[73] !== label || $[74] !== labelClass || $[75] !== name || $[76] !== radioOptions || $[77] !== required || $[78] !== showPassword || $[79] !== showPasswordIcon || $[80] !== showValueToggleIcon || $[81] !== type || $[82] !== value) {
    t8 = type === InputType.CHECKBOX ? <div className={classNames(styles["form-group__input-wrapper"], styles["form-group__input-wrapper--checkbox"])}><FormGroupField {...fieldProps} /><label className={getLabelClass()} htmlFor={inputId}>{label}{required && !hideRequiredAsterisk && <span> *</span>}</label></div> : type === InputType.RADIO ? <div className={classNames(styles["form-group__input-wrapper"], styles["form-group__input-wrapper--radio"])}><span className={getLabelClass()}>{label}{required && !hideRequiredAsterisk && <span> *</span>}</span><div className={styles["form-group__radio-options"]}>{radioOptions?.map(option_0 => {
          const radioOptionId = `${inputId}-${option_0.value.toString()}`;
          return <label key={option_0.value} className={`${styles["form-group__radio-option"]} text-base`} htmlFor={radioOptionId}><FormGroupField {...fieldProps} option={option_0} fieldId={radioOptionId} />{" "}{option_0.label}</label>;
        })}</div></div> : <label className={getLabelClass()}><span className={labelClass}>{label}{required && !hideRequiredAsterisk && <span> *</span>}</span><div className={inputWrapperClassName}><div className={inputFieldClassName}><FormGroupField {...fieldProps} />{value !== "" && showValueToggleIcon && type === InputType.PASSWORD && <button className={styles["form-group__input-icon-btn"]} type="button" title={name} onClick={() => setShowPassword(!showPassword)}><ImageComponent src={showPasswordIcon.path} alt={showPasswordIcon.alt} width={showPasswordIcon.width} height={showPasswordIcon.height} /></button>}</div>{hasInputButton && <div className={styles["form-group__input-button"]}>{inputButton}</div>}</div></label>;
    $[65] = fieldProps;
    $[66] = getLabelClass;
    $[67] = hasInputButton;
    $[68] = hideRequiredAsterisk;
    $[69] = inputButton;
    $[70] = inputFieldClassName;
    $[71] = inputId;
    $[72] = inputWrapperClassName;
    $[73] = label;
    $[74] = labelClass;
    $[75] = name;
    $[76] = radioOptions;
    $[77] = required;
    $[78] = showPassword;
    $[79] = showPasswordIcon;
    $[80] = showValueToggleIcon;
    $[81] = type;
    $[82] = value;
    $[83] = t8;
  } else {
    t8 = $[83];
  }
  let t9;
  if ($[84] !== error || $[85] !== errorBtn || $[86] !== errorTextId || $[87] !== hasError || $[88] !== isAutocompleteEnabled || $[89] !== name || $[90] !== passwordConditionsId || $[91] !== showPasswordConditions || $[92] !== showPasswordReuseCondition || $[93] !== touched || $[94] !== type || $[95] !== username || $[96] !== value) {
    t9 = showPasswordConditions ? <PasswordConditionsComponent newPasswordValue={value} errorMessage={hasError ? error : undefined} id={passwordConditionsId} showPasswordReuseCondition={showPasswordReuseCondition} username={username} /> : <FormGroupErrorText id={errorTextId} error={error} touched={touched} type={type} name={name} isAutocompleteEnabled={isAutocompleteEnabled} errorBtn={errorBtn} />;
    $[84] = error;
    $[85] = errorBtn;
    $[86] = errorTextId;
    $[87] = hasError;
    $[88] = isAutocompleteEnabled;
    $[89] = name;
    $[90] = passwordConditionsId;
    $[91] = showPasswordConditions;
    $[92] = showPasswordReuseCondition;
    $[93] = touched;
    $[94] = type;
    $[95] = username;
    $[96] = value;
    $[97] = t9;
  } else {
    t9 = $[97];
  }
  let t10;
  if ($[98] !== t7 || $[99] !== t8 || $[100] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[98] = t7;
    $[99] = t8;
    $[100] = t9;
    $[101] = t10;
  } else {
    t10 = $[101];
  }
  return t10;
}
export default memo(FormGroupComponent);